import { useNavigate } from '@tanstack/react-router';
import { cn } from '@utima/ui';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@/hooks';

import { ConnectionStatus } from './components/ConnectionStatus';
import { ExportModal } from './components/ExportModal';
import { InboxConversation } from './components/InboxConversation';
import { InboxSidebar } from './components/InboxSidebar';
import { ThreadDetails } from './components/ThreadDetails';
import { useInboxSocket } from './hooks/useInboxSocket';
import { useInboxThread } from './hooks/useInboxThread';
import { useInboxThreads } from './hooks/useInboxThreads';
import { useInboxUnread } from './hooks/useInboxUnread';

export interface InboxProps {
  initialThreadId?: string;
}

export const Inbox = memo(({ initialThreadId }: InboxProps) => {
  const navigate = useNavigate();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(
    initialThreadId ?? null,
  );
  const { markAsRead, isUnread } = useInboxUnread();
  const [showMetadata, setShowMetadata] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  /**
   * Fetch threads and selected thread
   */
  const { threads, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInboxThreads();

  const previousThreads = useRef(threads);

  const {
    thread,
    handleSubmit,
    hasConnectionFailed: hasThreadConnectionFailed,
    isLoading: isThreadLoading,
    isError: isThreadError,
  } = useInboxThread(selectedThreadId);

  const { refetchUnread } = useInboxUnread(true);

  /**
   * Initiate connection with admin websocket
   */
  const { hasConnectionFailed: hasAdminConnectionFailed } = useInboxSocket();

  useEffect(() => {
    if (initialThreadId) {
      setSelectedThreadId(initialThreadId);
    }
  }, [initialThreadId]);

  /**
   * Invalid / inaccessible deep-link threadId — drop it and fall back.
   */
  useEffect(() => {
    if (!selectedThreadId || !isThreadError) {
      return;
    }

    const fallbackId = threads[0]?.id ?? null;
    setSelectedThreadId(fallbackId);
    void navigate({
      to: '/inbox',
      search: fallbackId ? { threadId: fallbackId } : {},
      replace: true,
    });
  }, [isThreadError, selectedThreadId, threads, navigate]);

  /**
   * Select first thread as default when no thread is selected.
   * Only on desktop — on mobile the list should be the initial view.
   */
  useEffect(() => {
    if (threads.length && !selectedThreadId && isDesktop) {
      setSelectedThreadId(threads[0].id);
    }
  }, [threads, selectedThreadId, isDesktop]);

  /**
   * Set the selected thread, sync URL, and mark it as read.
   */
  const handleThreadSelect = useCallback(
    (threadId: string) => {
      setSelectedThreadId(threadId);
      void navigate({
        to: '/inbox',
        search: { threadId },
        replace: true,
      });
    },
    [navigate],
  );

  /**
   * Clear the selected thread (mobile back navigation).
   */
  const handleThreadDeselect = useCallback(() => {
    setSelectedThreadId(null);
    void navigate({
      to: '/inbox',
      search: {},
      replace: true,
    });
  }, [navigate]);

  useEffect(() => {
    if (selectedThreadId && isUnread(selectedThreadId)) {
      markAsRead(selectedThreadId);
    }
  }, [selectedThreadId, markAsRead, isUnread]);

  useEffect(() => {
    if (previousThreads.current !== threads) {
      refetchUnread();
      previousThreads.current = threads;
    }
  }, [previousThreads, refetchUnread, threads]);

  return (
    <>
      <div className='flex h-screen flex-col overflow-hidden bg-white'>
        {(hasThreadConnectionFailed || hasAdminConnectionFailed) && (
          <ConnectionStatus className='mx-4 mt-4 shrink-0 shadow' />
        )}
        <div className='flex min-h-0 flex-1 overflow-hidden'>
          <div
            className={cn(
              'w-full border-r border-gray-200 md:block md:w-[180px] lg:w-[300px] xl:w-[360px]',
              selectedThreadId ? 'hidden md:block' : 'block',
            )}
          >
            <InboxSidebar
              threads={threads}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              onFetchNextPage={fetchNextPage}
              activeThreadId={selectedThreadId}
              onSelectThread={handleThreadSelect}
              hasNextPage={hasNextPage}
              isUnread={isUnread}
              onExportClick={() => setShowExportModal(() => true)}
            />
          </div>
          <div
            className={cn(
              'w-full flex-1 overflow-hidden',
              selectedThreadId ? 'block' : 'hidden md:block',
            )}
          >
            <InboxConversation
              onSubmit={handleSubmit}
              thread={thread}
              isLoading={isThreadLoading}
              onToggleMetadata={() => setShowMetadata(v => !v)}
              onBack={handleThreadDeselect}
            />
          </div>
          {showMetadata && thread && (
            <ThreadDetails
              thread={thread}
              onClose={() => setShowMetadata(false)}
            />
          )}
        </div>
      </div>
      <ExportModal
        isOpen={showExportModal}
        onCloseClick={() => setShowExportModal(() => false)}
      />
    </>
  );
});
